import { getWebSocketBaseURL, playNotificationSound } from "./utils";

export const WS_LOG_ENABLED = false;

/** Логи состояния WebSocket (подключение, сообщения, вызовы). Включить для отладки на деплое. */
export const WS_STATE_LOG_ENABLED = false;

const stateLog = (label, data) => {
  if (!WS_STATE_LOG_ENABLED) return;
  if (data != null && typeof data === "object") {
    console.log("[WS state]", label, data);
  } else {
    console.log("[WS state]", label, data != null ? data : "");
  }
};

export const wsLogger = {
  incoming: (event, data) => {
    if (!WS_LOG_ENABLED) return;
    console.groupCollapsed(`[WS ←] ${event}`);
    console.log("Данные:", data);
    console.log("Время:", new Date().toISOString());
    console.groupEnd();
  },
  outgoing: (event, payload) => {
    if (!WS_LOG_ENABLED) return;
    console.groupCollapsed(`[WS →] ${event}`);
    console.log("Отправка:", payload);
    console.log("Время:", new Date().toISOString());
    console.groupEnd();
  },
  error: (error, context) => {
    if (!WS_LOG_ENABLED) return;
    console.error(`[WS ✗] Ошибка в ${context}:`, error);
    console.error("[WS ✗] Детали ошибки:", {
      message: error?.message,
      name: error?.name,
      stack: error?.stack,
      target: error?.target,
      type: error?.type,
      timeStamp: error?.timeStamp,
      context,
      url: error?.target?.url,
      readyState: error?.target?.readyState,
      timestamp: new Date().toISOString(),
    });
  },
  info: (message, data) => {
    if (!WS_LOG_ENABLED) return;
    console.log(`[WS ℹ] ${message}`, data || "");
  },
  debug: (message, data) => {
    if (!WS_LOG_ENABLED) return;
    console.debug(`[WS 🔍] ${message}`, data || "");
  },
  warn: (message, data) => {
    if (!WS_LOG_ENABLED) return;
    console.warn(`[WS ⚠] ${message}`, data || "");
  },
};

export class ChatWebSocket {
  constructor(url, handlers) {
    this.url = url;
    this.handlers = handlers;
    this.ws = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.reconnectTimeout = null;
    this.isClosing = false;
  }

  connect() {
    if (!this.url) {
      wsLogger.warn("Попытка подключения без URL");
      return;
    }

    wsLogger.debug("Проверка состояния перед подключением", {
      isConnected: this.isConnected,
      wsExists: !!this.ws,
      wsReadyState: this.ws?.readyState,
      wsReadyStateText: this.ws
        ? ["CONNECTING", "OPEN", "CLOSING", "CLOSED"][this.ws.readyState]
        : "N/A",
      isClosing: this.isClosing,
      url: this.url,
    });

    if (
      this.isConnected ||
      (this.ws &&
        (this.ws.readyState === WebSocket.CONNECTING ||
          this.ws.readyState === WebSocket.CLOSING)) ||
      this.isClosing
    ) {
      wsLogger.debug("Подключение пропущено - уже подключен или в процессе", {
        isConnected: this.isConnected,
        readyState: this.ws?.readyState,
        isClosing: this.isClosing,
      });
      return;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
      wsLogger.debug("Отменен таймаут переподключения");
    }

    this.isClosing = false;

    try {
      stateLog("CONNECTING", { url: this.url });
      wsLogger.info("Создание нового WebSocket соединения", {
        url: this.url,
        timestamp: new Date().toISOString(),
        reconnectAttempt: this.reconnectAttempts,
        environment: {
          hostname: window.location.hostname,
          protocol: window.location.protocol,
          REACT_APP_APP_URL: process.env.REACT_APP_APP_URL,
        },
      });

      const startTime = Date.now();
      this.ws = new WebSocket(this.url);

      wsLogger.debug("WebSocket объект создан", {
        url: this.url,
        readyState: this.ws.readyState,
        readyStateText: ["CONNECTING", "OPEN", "CLOSING", "CLOSED"][
          this.ws.readyState
        ],
        protocol: this.ws.protocol,
        extensions: this.ws.extensions,
      });

      this.setupEventListeners();

      
      setTimeout(() => {
        if (this.ws) {
          wsLogger.debug("Состояние WebSocket через 100ms", {
            readyState: this.ws.readyState,
            readyStateText: ["CONNECTING", "OPEN", "CLOSING", "CLOSED"][
              this.ws.readyState
            ],
            timeElapsed: Date.now() - startTime,
          });
        }
      }, 100);
    } catch (error) {
      wsLogger.error(error, "Создание WebSocket");
      wsLogger.error("Детали ошибки при создании", {
        errorType: error?.constructor?.name,
        errorMessage: error?.message,
        url: this.url,
        stack: error?.stack,
      });
      this.scheduleReconnect();
    }
  }

  setupEventListeners() {
    if (!this.ws) {
      wsLogger.warn("setupEventListeners вызван без WebSocket объекта");
      return;
    }

    this.ws.onopen = (event) => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      stateLog("OPEN", { url: this.url, readyState: this.ws.readyState });
      wsLogger.info("WebSocket успешно подключен", {
        url: this.url,
        readyState: this.ws.readyState,
        protocol: this.ws.protocol,
        extensions: this.ws.extensions,
        timestamp: new Date().toISOString(),
      });
      if (this.handlers.onOpen) this.handlers.onOpen();
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        stateLog("MESSAGE", {
          type: data?.type,
          chat_id: data?.chat_id,
          event: data?.event,
        });
        wsLogger.incoming("Получено сообщение от сервера", data);
        if (this.handlers.onMessage) this.handlers.onMessage(data);
      } catch (err) {
        wsLogger.error(err, "Обработка WebSocket сообщения");
        wsLogger.debug("Необработанные данные сообщения", {
          rawData: event.data,
          dataType: typeof event.data,
          dataLength: event.data?.length,
        });
        if (this.handlers.onError) this.handlers.onError(err);
      }
    };

    this.ws.onclose = (event) => {
      this.isConnected = false;
      this.isClosing = false;
      stateLog("CLOSE", {
        url: this.url,
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      });

      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;

      if (this.handlers.onClose) this.handlers.onClose(event);

      if (
        !event.wasClean &&
        event.code !== 1000 &&
        !this.isClosing &&
        !this.reconnectTimeout
      ) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = (error) => {
      stateLog("ERROR", { url: this.url, readyState: this.ws?.readyState });
      wsLogger.error(error, "Ошибка WebSocket");
      wsLogger.error("Детали ошибки onerror", {
        errorType: error?.type,
        target: error?.target,
        targetURL: error?.target?.url,
        targetReadyState: error?.target?.readyState,
        readyStateText: error?.target
          ? ["CONNECTING", "OPEN", "CLOSING", "CLOSED"][error.target.readyState]
          : "N/A",
        url: this.url,
        isConnected: this.isConnected,
        timestamp: new Date().toISOString(),
      });
      if (this.handlers.onError) this.handlers.onError(error);
    };

    wsLogger.debug("Event listeners установлены", {
      url: this.url,
      hasOnOpen: !!this.ws.onopen,
      hasOnMessage: !!this.ws.onmessage,
      hasOnClose: !!this.ws.onclose,
      hasOnError: !!this.ws.onerror,
    });
  }

  send(payload) {
    if (this.isConnected && this.ws?.readyState === WebSocket.OPEN) {
      const stringPayload =
        typeof payload === "string" ? payload : JSON.stringify(payload);
      this.ws.send(stringPayload);
      wsLogger.outgoing("Отправка сообщения", payload);
      return true;
    }
    wsLogger.error("WebSocket не подключен", "send");
    return false;
  }

  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      wsLogger.warn(
        "Превышено максимальное количество попыток переподключения",
        {
          attempts: this.reconnectAttempts,
          maxAttempts: this.maxReconnectAttempts,
          url: this.url,
          timestamp: new Date().toISOString(),
        },
      );
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    const actualDelay = Math.min(delay, 30000);

    wsLogger.info(
      `Попытка переподключения ${this.reconnectAttempts}/${this.maxReconnectAttempts} через ${actualDelay}ms`,
      {
        attempt: this.reconnectAttempts,
        maxAttempts: this.maxReconnectAttempts,
        calculatedDelay: delay,
        actualDelay: actualDelay,
        url: this.url,
        timestamp: new Date().toISOString(),
      },
    );

    this.reconnectTimeout = setTimeout(() => {
      wsLogger.debug("Таймаут переподключения истек", {
        isConnected: this.isConnected,
        isClosing: this.isClosing,
        url: this.url,
      });
      if (!this.isConnected && !this.isClosing) {
        this.connect();
      } else {
        wsLogger.debug("Переподключение отменено", {
          isConnected: this.isConnected,
          isClosing: this.isClosing,
        });
      }
    }, actualDelay);
  }

  disconnect() {
    this.isClosing = true;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      if (
        this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING
      ) {
        this.ws.close(1000, "Нормальное закрытие");
      }
    }
  }
}

let wsManagerInstance = null;

export class WebSocketManager {
  constructor() {
    if (wsManagerInstance) {
      return wsManagerInstance;
    }

    this.allChatsSocket = null;
    this.chatSockets = new Map();
    this.allChatsToken = null;
    this.allChatsHandlers = null;
    this.isAllChatsConnecting = false;

    this.typingStatus = {};
    this.onlineStatus = {};
    this.lastMessageUpdates = {};

    this.onTypingUpdate = null;
    this.onOnlineUpdate = null;
    this.onLastMessageUpdate = null;
    this.onNewMessage = null;
    this.onChatUpdated = null;

    wsManagerInstance = this;
  }

  static getInstance() {
    if (!wsManagerInstance) {
      wsManagerInstance = new WebSocketManager();
    }
    return wsManagerInstance;
  }

  setCallbacks(callbacks) {
    this.onTypingUpdate = callbacks.onTypingUpdate;
    this.onOnlineUpdate = callbacks.onOnlineUpdate;
    this.onLastMessageUpdate = callbacks.onLastMessageUpdate;
    this.onNewMessage = callbacks.onNewMessage;
    this.onChatUpdated = callbacks.onChatUpdated;
  }

  updateTypingStatus(chatId, isTyping, userType, userId) {
    if (!chatId) return;
    const timestamp = new Date().toISOString();

    if (!this.typingStatus[chatId]) {
      this.typingStatus[chatId] = {};
    }

    const key = String(userId || userType || "unknown");

    if (isTyping) {
      this.typingStatus[chatId][key] = {
        isTyping: true,
        userType,
        userId,
        timestamp,
      };

      
      setTimeout(() => {
        const entry = this.typingStatus[chatId]?.[key];
        if (entry && entry.timestamp === timestamp) {
          delete this.typingStatus[chatId][key];
          if (this.onTypingUpdate) {
            this.onTypingUpdate(chatId, this.getTypingInfo(chatId));
          }
        }
      }, 5000);
    } else {
      delete this.typingStatus[chatId]?.[key];
    }

    if (this.onTypingUpdate) {
      this.onTypingUpdate(chatId, this.getTypingInfo(chatId));
    }
  }

  getTypingInfo(chatId) {
    const users = Object.values(this.typingStatus[chatId] || {});
    const typing = users.filter((u) => u.isTyping);
    return {
      isTyping: typing.length > 0,
      users: typing, 
      count: typing.length,
    };
  }

  
  getTypingStatus(chatId) {
    return this.getTypingInfo(chatId);
  }

  updateOnlineStatus(chatId, userId, userType, isConnected) {
    if (!chatId || !userId) return;

    if (!this.onlineStatus[chatId]) {
      this.onlineStatus[chatId] = {};
    }

    this.onlineStatus[chatId][userId] = { userType, connected: isConnected };

    if (this.onOnlineUpdate) {
      this.onOnlineUpdate(chatId, { ...this.onlineStatus[chatId] });
    }
  }

  updateLastMessage(chatId, message, messageType, timestamp, senderType) {
    if (!chatId) return;

    this.lastMessageUpdates[chatId] = {
      last_message: message,
      last_message_type: messageType,
      last_message_time: timestamp,
      last_sender_type: senderType,
    };

    if (this.onLastMessageUpdate) {
      this.onLastMessageUpdate(chatId, this.lastMessageUpdates[chatId]);
    }
  }

  sendTypingIndicator(chatId, isTyping, userType) {
    const socket = this.chatSockets.get(chatId);
    if (socket && socket.isConnected) {
      const payload = {
        type: "typing",
        is_typing: isTyping,
        user_type: userType,
        chat_id: chatId,
      };
      return socket.send(payload);
    }
    return false;
  }

  createAllChatsHandler(queryClient, getSenderType, processImageData) {
    return (data) => {
      switch (data.type) {
        case "connected":
          wsLogger.info("Успешно подключились ко всем чатам", data);
          break;

        case "chat_typing":
          if (data.chat_id && data.user_type) {
            this.updateTypingStatus(
              data.chat_id,
              data.is_typing,
              data.user_type,
              data.user_id,
            );
          }
          break;

        case "chat_message":
          if (data.event === "new_chat") {
            if (this.onChatUpdated) this.onChatUpdated(data.chat_id);
            break;
          }
          if (data.status === "DELIVERED" || data.event === "new_message") {
            const isFromClient =
              String(data.sender_type || "").toUpperCase() === "CLIENT";
            if (isFromClient) {
              playNotificationSound();
            }
            if (
              !this.chatSockets.has(data.chat_id) &&
              this.onMessageStatusUpdate
            ) {
              this.onMessageStatusUpdate({
                chat_id: data.chat_id,
                client_message_id: data.client_message_id || data.message_id,
                server_message_id: data.message_id,
                status: data.status,
                timestamp: data.timestamp,
              });
            }
            if (this.onNewMessage) {
              const ts = data.timestamp || new Date().toISOString();
              this.onNewMessage({
                id: data.message_id,
                chat_id: data.chat_id,
                sender_type: data.sender_type,
                content:
                  data.message_type === "IMAGE" ? "" : data.content || "",
                message_type: data.message_type || "TEXT",
                status: data.status || "DELIVERED",
                created_at: ts,
                updated_at: ts,
                source: "WEB",
                image_url: data.message_type === "IMAGE" ? data.content : null,
              });
            }
            this.updateLastMessage(
              data.chat_id,
              data.content,
              data.message_type,
              data.timestamp,
              data.sender_type,
            );
          }
          break;

        case "chat_updated":
          if (this.onChatUpdated) {
            this.onChatUpdated(data.chat_id);
          }
          break;

        case "user_connected":
          if (data.chat_id && data.user_id && data.user_type) {
            this.updateOnlineStatus(
              data.chat_id,
              data.user_id,
              data.user_type,
              true,
            );
          }
          break;

        case "user_disconnected":
          if (data.chat_id && data.user_id && data.user_type) {
            this.updateOnlineStatus(
              data.chat_id,
              data.user_id,
              data.user_type,
              false,
            );
          }
          break;

        case "message_updated":
          break;

        default:
          if (data.event === "new_chat" && data.chat_id && this.onChatUpdated) {
            this.onChatUpdated(data.chat_id);
          }
          break;
      }
    };
  }

  createChatHandler(chatId, queryClient, getSenderType, processImageData) {
    return (data) => {
      switch (data.type) {
        case "connected":
          break;

        case "user_connected":
          if (data.chat_id && data.user_id && data.user_type) {
            this.updateOnlineStatus(
              data.chat_id,
              data.user_id,
              data.user_type,
              true,
            );
          }
          break;

        case "user_disconnected":
          if (data.chat_id && data.user_id && data.user_type) {
            this.updateOnlineStatus(
              data.chat_id,
              data.user_id,
              data.user_type,
              false,
            );
          }
          break;

        case "message":
          if (data.status === "DELIVERED") {
            if (this.onNewMessage) {
              const message = {
                id: data.message_id || data.id,
                chat_id: data.chat_id,
                sender_type: data.sender_type,
                content: data.message_type === "IMAGE" ? "" : data.content,
                message_type: data.message_type || "TEXT",
                status: data.status || "SENT",
                created_at: data.timestamp || new Date().toISOString(),
                updated_at: data.timestamp || new Date().toISOString(),
                source: "WEB",
                image_url:
                  data.message_type === "IMAGE"
                    ? processImageData(data.content)
                    : null,
              };
              this.onNewMessage(message);
            }

            this.updateLastMessage(
              data.chat_id,
              data.content,
              data.message_type,
              data.timestamp,
              data.sender_type,
            );

            this.updateTypingStatus(
              data.chat_id,
              false,
              data.sender_type,
              data.user_id,
            );
          }
          break;

        case "typing":
          if (data.chat_id && data.user_type) {
            this.updateTypingStatus(
              data.chat_id,
              data.is_typing,
              data.user_type,
              data.user_id,
            );
          }
          break;

        default:
          break;
      }
    };
  }

  connectToAllChats(token, handlers) {
    wsLogger.debug("connectToAllChats вызван", {
      hasToken: !!token,
      tokenLength: token?.length,
      hasHandlers: !!handlers,
      currentToken: this.allChatsToken,
      hasExistingSocket: !!this.allChatsSocket,
      isConnecting: this.isAllChatsConnecting,
      timestamp: new Date().toISOString(),
    });

    if (!token) {
      wsLogger.error(
        "Токен не предоставлен для подключения ко всем чатам",
        "connectToAllChats",
      );
      return null;
    }

    if (this.allChatsToken === token && this.allChatsSocket) {
      if (handlers !== this.allChatsHandlers) {
        this.allChatsSocket.handlers = handlers;
        this.allChatsHandlers = handlers;
      }

      if (
        this.allChatsSocket.isConnected ||
        (this.allChatsSocket.ws &&
          this.allChatsSocket.ws.readyState === WebSocket.CONNECTING)
      ) {
        return this.allChatsSocket;
      }

      if (
        !this.allChatsSocket.isConnected &&
        !this.allChatsSocket.isClosing &&
        !this.isAllChatsConnecting &&
        !this.allChatsSocket.reconnectTimeout
      ) {
        this.allChatsSocket.connect();
      }

      return this.allChatsSocket;
    }

    if (this.allChatsSocket) {
      wsLogger.info("Закрываем старое /all/ соединение (изменился токен)", {
        oldToken: this.allChatsToken,
        newToken: token,
        wasConnected: this.allChatsSocket.isConnected,
      });
      this.allChatsSocket.disconnect();
      this.allChatsSocket = null;
      this.allChatsToken = null;
      this.allChatsHandlers = null;
    }

    const baseURL = getWebSocketBaseURL();
    const url = `${baseURL}/ws/chats/all/${token}/`;

    stateLog("connectToAllChats", {
      url: url.replace(token, "***"),
      hasToken: !!token,
    });
    wsLogger.info("Создание нового соединения ко всем чатам", {
      url: url,
      baseURL: baseURL,
      tokenLength: token.length,
      tokenPreview: token.substring(0, 10) + "...",
      environment: {
        hostname: window.location.hostname,
        protocol: window.location.protocol,
        REACT_APP_APP_URL: process.env.REACT_APP_APP_URL,
      },
      timestamp: new Date().toISOString(),
    });

    if (this.isAllChatsConnecting) {
      wsLogger.warn("Подключение ко всем чатам уже в процессе", {
        url: url,
        timestamp: new Date().toISOString(),
      });
      return null;
    }

    this.isAllChatsConnecting = true;
    this.allChatsToken = token;
    this.allChatsHandlers = handlers;

    this.allChatsSocket = new ChatWebSocket(url, {
      ...handlers,
      onOpen: () => {
        wsLogger.debug("onOpen callback для allChatsSocket", {
          url: url,
          timestamp: new Date().toISOString(),
        });
        this.isAllChatsConnecting = false;
        if (handlers.onOpen) handlers.onOpen();
      },
      onClose: (event) => {
        wsLogger.debug("onClose callback для allChatsSocket", {
          url: url,
          code: event.code,
          wasClean: event.wasClean,
          timestamp: new Date().toISOString(),
        });
        this.isAllChatsConnecting = false;
        if (event.code !== 1000) {
          wsLogger.warn("Соединение закрыто с ошибкой, очищаем состояние", {
            code: event.code,
            wasClean: event.wasClean,
          });
          this.allChatsSocket = null;
          this.allChatsToken = null;
          this.allChatsHandlers = null;
        }
        if (handlers.onClose) handlers.onClose(event);
      },
      onError: (error) => {
        wsLogger.error("onError callback для allChatsSocket", error);
        this.isAllChatsConnecting = false;
        if (handlers.onError) handlers.onError(error);
      },
    });

    this.allChatsSocket.connect();

    return this.allChatsSocket;
  }

  connectToChat(chatId, token, handlers) {
    wsLogger.debug("connectToChat вызван", {
      chatId: chatId,
      hasToken: !!token,
      tokenLength: token?.length,
      hasHandlers: !!handlers,
      timestamp: new Date().toISOString(),
    });

    if (!chatId || !token) {
      wsLogger.warn("connectToChat вызван без chatId или token", {
        chatId: chatId,
        hasToken: !!token,
      });
      return null;
    }

    const baseURL = getWebSocketBaseURL();
    const url = `${baseURL}/ws/chats/${chatId}/${token}/`;

    wsLogger.info("Создание соединения к чату", {
      chatId: chatId,
      url: url,
      baseURL: baseURL,
      tokenLength: token.length,
      timestamp: new Date().toISOString(),
    });

    let socket = this.chatSockets.get(chatId);

    if (socket) {
      wsLogger.info(
        `Обновляем handlers для существующего сокета чата ${chatId}`,
      );
      socket.handlers = handlers;

      if (!socket.isConnected && !socket.isClosing) {
        socket.connect();
      }

      return socket;
    }

    socket = new ChatWebSocket(url, handlers);
    socket.connect();
    this.chatSockets.set(chatId, socket);

    return socket;
  }

  getSocket(chatId) {
    return this.chatSockets.get(chatId);
  }

  disconnectFromChat(chatId) {
    if (this.chatSockets.has(chatId)) {
      const socket = this.chatSockets.get(chatId);
      socket.disconnect();
      this.chatSockets.delete(chatId);
      wsLogger.info(`Отключили WebSocket для чата ${chatId}`);
    }
  }

  disconnectAllChats() {
    wsLogger.info("Отключаем WebSocket соединение для всех чатов", {
      hasSocket: !!this.allChatsSocket,
      isConnected: this.allChatsSocket?.isConnected,
      readyState: this.allChatsSocket?.ws?.readyState,
      isConnecting: this.isAllChatsConnecting,
    });

    if (this.allChatsSocket) {
      const shouldDisconnect =
        this.allChatsSocket.isConnected ||
        this.allChatsSocket.ws?.readyState === WebSocket.OPEN ||
        this.allChatsSocket.ws?.readyState === WebSocket.CONNECTING;

      if (shouldDisconnect) {
        wsLogger.debug("Отключаем активное соединение");
        this.allChatsSocket.disconnect();
      } else {
        wsLogger.debug("Соединение не установлено, просто очищаем ссылку");
      }

      this.allChatsSocket = null;
      this.allChatsToken = null;
      this.allChatsHandlers = null;
      this.isAllChatsConnecting = false;
    }
  }

  disconnectAll() {
    wsLogger.info("Отключаем все WebSocket соединения");

    if (this.allChatsSocket) {
      this.allChatsSocket.disconnect();
      this.allChatsSocket = null;
      this.allChatsToken = null;
    }

    this.chatSockets.forEach((socket, chatId) => {
      socket.disconnect();
      wsLogger.info(`Отключили WebSocket для чата ${chatId}`);
    });
    this.chatSockets.clear();
  }

  getOnlineStatus(chatId) {
    const chatOnline = this.onlineStatus[chatId];
    if (!chatOnline) return false;

    return Object.values(chatOnline).some(
      (user) => user.userType === "CLIENT" && user.connected,
    );
  }

  getLastMessageUpdates(chatId) {
    return this.lastMessageUpdates[chatId];
  }
}
